/**
 * El resultado contra el control en palabras (fase 5, diseño `Resultado.dc.html`, «Detalle», y las tarjetas de
 * Campañas e Inicio). Lo que lee el cliente lo arma el backend, con las mismas palabras que el PDF: acá van la tabla
 * por grupo, la comparación, los avisos y el estado. Puro y testeado (`resultado.test.ts`).
 */

import { num, plural } from "../../campana-nueva/utils/format";
import type { EstadoCampana, Recorrido, Resultado, ResultadoDelGrupo, ResumenDelResultado } from "../types";

/** La hora del comercio (como `TZ_NEGOCIO` del backend). */
const TZ = "America/Argentina/Buenos_Aires";

/** «8,4»: una cifra decimal, como en el diseño. */
export const decimal = (n: number) => Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** «+0,4», «−0,2» (con el signo menos tipográfico) y «0,0». */
export const conSigno = (n: number) => (n > 0 ? `+${decimal(n)}` : n < 0 ? `−${decimal(-n)}` : decimal(0));
export const pesos = (n: number) => `${n < 0 ? "−" : ""}$${num(Math.abs(Math.round(n)))}`;

/** «06/10» de un instante, en la hora del comercio. */
export function diaMesDe(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: TZ, day: "2-digit", month: "2-digit" }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.day}/${p.month}`;
}

export type TonoEstado = "violeta" | "verde" | "ambar" | "gris";

/** La píldora del estado: «Midiendo hasta el 20/10». */
export function estadoEnPalabras(e: { estado: EstadoCampana; midiendoHasta: string | null }): { texto: string; tono: TonoEstado } {
  const hasta = diaMesDe(e.midiendoHasta);
  switch (e.estado) {
    case "preparada":
      return { texto: "Preparada · todavía no salió", tono: "gris" };
    case "enviando":
      return { texto: "Enviando", tono: "violeta" };
    case "midiendo":
      return { texto: hasta ? `Midiendo hasta el ${hasta}` : "Midiendo", tono: "verde" };
    case "esperando_base":
      return { texto: "Esperando la base nueva", tono: "ambar" };
    default:
      return { texto: "Terminada", tono: "gris" };
  }
}

/** «Salió el 06/10 · medimos hasta el 20/10». */
export function textoDeLasFechas(e: { estado: EstadoCampana; primerEnvio: string | null; midiendoHasta: string | null }): string {
  const salio = diaMesDe(e.primerEnvio);
  const hasta = diaMesDe(e.midiendoHasta);
  if (!salio) return "Todavía no salió nada";
  return e.estado === "terminada" ? `Salió el ${salio} · se midió hasta el ${hasta}` : `Salió el ${salio} · medimos hasta el ${hasta}`;
}

/** Cuánto le llegó a un grupo: «2.682 · completo», «2.250 de 5.942» o «No salió». */
export function textoEnvio(g: { recibieron: number; faltoMandar: number }): string {
  if (g.recibieron === 0) return "No salió";
  if (g.faltoMandar === 0) return `${num(g.recibieron)} · completo`;
  return `${num(g.recibieron)} de ${num(g.recibieron + g.faltoMandar)}`;
}

/** Las columnas de «Por grupo»: compraron de cada 1.000 (recibieron vs. no), compradores de más y si es firme. */
export function filaDelGrupo(g: ResultadoDelGrupo): { envio: string; tasas: string; deMas: string; firme: string } {
  const envio = textoEnvio({ recibieron: g.recibieron.personas, faltoMandar: g.faltoMandar });
  if (!g.medible) return { envio, tasas: "—", deMas: "—", firme: "—" };
  return { envio, tasas: `${decimal(g.recibieron.porMil)} vs ${decimal(g.control.porMil)}`, deMas: conSigno(g.compradoresDeMas), firme: g.firme ? "Sí" : "Todavía no" };
}

/** Debajo de las barras: si ya es firme, y si no, qué mejora se vería con este volumen. */
export function textoFirmeza(g: ResultadoDelGrupo): string {
  if (g.recibieron.personas === 0) return "Este grupo todavía no recibió nada.";
  if (!g.medible) return "Sin grupo de control no hay con qué comparar: este grupo no se puede medir.";
  if (g.firme) {
    return g.compradoresDeMas > 0
      ? `Es firme, con 90 % de confianza: ${conSigno(g.compradoresDeMas)} compradores de más, ${pesos(g.vendisteDeMas)} vendidos de más.`
      : "Es firme, con 90 % de confianza: quienes recibieron compraron menos que el control.";
  }
  if (g.mejoraMinima == null) return "Todavía no es firme: todavía no compró nadie.";
  return `Todavía no es firme: con este tamaño solo se ve una mejora de +${num(Math.round(g.mejoraMinima * 100))}% o más.`;
}

/** El ancho de cada barra, sobre la más alta de las dos (la más alta llega al 90 %). */
export function anchos(g: ResultadoDelGrupo): { recibieron: string; control: string } {
  const max = Math.max(g.recibieron.porMil, g.control.porMil);
  const ancho = (x: number) => (max > 0 ? `${Math.max(x > 0 ? 2 : 0, Math.round((x / max) * 90))}%` : "0%");
  return { recibieron: ancho(g.recibieron.porMil), control: ancho(g.control.porMil) };
}

/**
 * ¿La mayoría compró sin el cupón, con la misma vara que «firme» (z ≥ 1,645)? Es `sinCuponFirme` del backend
 * (`backend/src/modules/envios/util/medicion.util.ts`), que decide también «el mismo mensaje, sin cupón».
 */
const sinCuponFirme = (compraronSinCupon: number, compraron: number) => compraron > 0 && (2 * compraronSinCupon - compraron - 1) / Math.sqrt(compraron) >= 1.645;

/** Lo que dice el cupón: «16 de los 22 compraron sin usar el cupón: el descuento no fue lo que los trajo.». */
export function textoCupon(g: ResultadoDelGrupo): string | null {
  const r = g.recibieron;
  if (g.oferta <= 0 || r.compraron === 0) return null;
  if (sinCuponFirme(r.compraronSinCupon, r.compraron)) return `${num(r.compraronSinCupon)} de los ${num(r.compraron)} compraron sin usar el cupón: el descuento no fue lo que los trajo.`;
  return `${num(r.usaronCupon)} de los ${num(r.compraron)} ${plural(r.usaronCupon, "usó", "usaron")} el cupón.`;
}

export interface AvisoDelResultado {
  tono: "info" | "atencion" | "error";
  texto: string;
}

/** Los avisos de un grupo, del más importante al menos. */
export function avisosDelGrupo(g: ResultadoDelGrupo): AvisoDelResultado[] {
  const out: AvisoDelResultado[] = [];
  const c = g.control.contaminados;
  if (c > 0) {
    const sin = g.sinContaminados ? ` Sin ${plural(c, "esa persona", "esas personas")}, daría ${conSigno(g.sinContaminados.compradoresDeMas)} compradores de más.` : "";
    // por WhatsApp sólo pudo ser el cupón; por mail, también que Snov le haya mandado el mail
    const base = g.canal === "email" ? `${plural(c, "usó", "usaron")} el cupón o ${plural(c, "recibió", "recibieron")} el mail` : `${plural(c, "usó", "usaron")} el cupón`;
    // a quien le llegó otra campaña de la marca cerca de su ventana: lo que compró puede ser por esa otra
    const otra = g.control.otraCampana ?? 0;
    const que = otra > 0 ? `${base} o ${plural(c, "le", "les")} llegó otra campaña de la marca` : base;
    out.push({ tono: "info", texto: `${num(c)} ${plural(c, "persona", "personas")} del control ${que}. El número oficial no cambia.${sin}` });
  }
  if (g.faltoMandar > 0 && g.recibieron.personas > 0) {
    const sinSi = g.faltoSinSi ?? 0;
    const porQue = sinSi > 0 ? ` ${num(sinSi)} no ${plural(sinSi, "tiene", "tienen")} el sí: el WhatsApp oficial no les escribe.` : "";
    // si la mayoría no tiene el sí, no es una falla del envío: es lo que se puede mandar
    // terminado el envío: cuánto daría con todo el grupo (los primeros en recibir son los que más compran)
    const todo = g.conElGrupoEntero ? ` Con todo el grupo, también los que no recibieron, daría ${conSigno(g.conElGrupoEntero.compradoresDeMas)} compradores de más: los primeros en recibir son los que más compran.` : "";
    out.push({ tono: sinSi * 2 >= g.faltoMandar ? "atencion" : "error", texto: `Faltan ${num(g.faltoMandar)} ${plural(g.faltoMandar, "persona", "personas")}: no ${plural(g.faltoMandar, "recibió", "recibieron")} y no entran en la cuenta.${porQue}${todo}` });
  }
  if (g.compraronAntes > 0) out.push({ tono: "info", texto: `${num(g.compraronAntes)} ${plural(g.compraronAntes, "compró", "compraron")} antes de que ${plural(g.compraronAntes, "le", "les")} llegara. No se cuentan.` });
  if (g.sinFicha > 0) out.push({ tono: "atencion", texto: `${num(g.sinFicha)} ya no ${plural(g.sinFicha, "está", "están")} en la base de ventas: ${plural(g.sinFicha, "cuenta", "cuentan")} como que no compraron.` });
  if (g.sinDatoDelSi) out.push({ tono: "atencion", texto: "Este envío se preparó antes de guardar quién tiene el sí: el control se compara entero." });
  return out;
}

/** Los avisos de toda la campaña (la base). */
export function avisosDeLaCampana(r: Pick<Resultado, "estado" | "base" | "avisos">): AvisoDelResultado[] {
  const out: AvisoDelResultado[] = [];
  if (!r.base) out.push({ tono: "error", texto: "No hay una base de ventas en uso: sin ella no se ve quién compró." });
  else if (r.estado.estado === "esperando_base") {
    out.push({ tono: "atencion", texto: `La medición cerró el ${diaMesDe(r.estado.midiendoHasta)}, pero la base llega hasta el ${(r.base.hasta ?? "").slice(8, 10)}/${(r.base.hasta ?? "").slice(5, 7)}: subí una base nueva para terminarla.` });
  }
  if (r.avisos.baseNoEmpieza) out.push({ tono: "atencion", texto: "La base en uso empieza después del primer envío: le faltan compras del principio." });
  return out;
}

/** Las cuatro cifras de la tarjeta de una campaña (Campañas e Inicio). «Vendiste de más» sin firmeza es «≈ $0». */
export function cifrasDeLaCampana(r: ResumenDelResultado): { titulo: string; valor: string }[] {
  return [
    { titulo: "Recibieron", valor: num(r.recibieron) },
    { titulo: "Compraron", valor: num(r.compraron) },
    { titulo: "Facturaron", valor: pesos(r.facturacion) },
    { titulo: "Vendiste de más", valor: r.firme && r.vendisteDeMas > 0 ? pesos(r.vendisteDeMas) : "≈ $0" },
  ];
}

/** «Salió el 06/10 · 2 de 4 grupos» */
export function textoSalida(r: ResumenDelResultado, gruposDeLaCampana: number): string {
  const salieron = r.grupos.filter((g) => g.recibieron > 0).length;
  const salio = diaMesDe(r.primerEnvio);
  return salio ? `Salió el ${salio} · ${num(salieron)} de ${num(gruposDeLaCampana)} ${plural(gruposDeLaCampana, "grupo", "grupos")}` : "Todavía no salió nada";
}

/** Cómo le fue a cada grupo de la campaña en el envío: «enviada», «salió al 38%» o «no salió». */
export function salidaDelGrupo(g: { recibieron: number; faltoMandar: number } | undefined): string {
  if (!g || g.recibieron === 0) return "no salió";
  if (g.faltoMandar === 0) return "enviada";
  return `salió al ${Math.round((g.recibieron * 100) / (g.recibieron + g.faltoMandar))}%`;
}

/* ── El recorrido (fase 5c) ──────────────────────────────────────────────────────────────────── */

export interface PasoDelRecorrido {
  titulo: string;
  valor: string;
  /** sobre los enviados («98%», «1,4%»); null sin el dato */
  pct: string | null;
  /** el ancho de la barra, sobre los enviados */
  ancho: string;
}

/** «98%» o, debajo del 10 %, con un decimal («1,4%»). */
export function porcentaje(parte: number, total: number): string {
  if (!(total > 0)) return "0%";
  const x = (parte * 100) / total;
  return x >= 10 ? `${num(Math.round(x))}%` : `${decimal(x)}%`;
}

/**
 * Los pasos del recorrido, como el diseño: por mail, enviados → les llegó → hicieron clic → compraron → usaron el
 * cupón (sin aperturas: los celulares las marcan solas); por WhatsApp, enviados → les llegó → lo leyeron →
 * respondieron → compraron → usaron el cupón.
 */
export function pasosDelRecorrido(r: Recorrido, canal: "whatsapp" | "email"): PasoDelRecorrido[] {
  const paso = (titulo: string, n: number | null): PasoDelRecorrido => ({
    titulo,
    valor: n === null ? "—" : num(n),
    pct: n === null ? null : porcentaje(n, r.enviados),
    ancho: n === null || !(r.enviados > 0) ? "0%" : `${Math.max(n > 0 ? 1 : 0, Math.round((n * 100) / r.enviados))}%`,
  });
  const medio = canal === "email" ? [paso("Hicieron clic", r.clic)] : [paso("Lo leyeron", r.leyeron), paso("Respondieron", r.respondieron)];
  return [paso("Enviados", r.enviados), paso("Les llegó", r.llego), ...medio, paso("Compraron", r.compraron), paso("Usaron el cupón", r.usaronCupon)];
}

/** Lo que dice el recorrido (ARQUITECTURA §7, «el embudo, como diagnóstico»). */
export function lecturaDelRecorrido(r: Recorrido, canal: "whatsapp" | "email"): string[] {
  const out: string[] = [];
  if (canal === "email" && r.clic !== null && r.enviados >= 100 && r.clic * 100 < r.enviados * 3) {
    out.push(`Casi nadie entra: ${decimal((r.clic * 1000) / r.enviados)} de cada 1.000 hicieron clic. Ahí está la mejora, no en el descuento.`);
  }
  if (r.sinRecibo > 0) out.push(`${num(r.sinRecibo)} ${plural(r.sinRecibo, "salió", "salieron")} por las líneas por QR, que no informan si llegó.`);
  return out;
}
